/**
 * Complementary Products Component
 * Handles dynamic loading and Swiper initialization for complementary product carousels
 */
class ComplementaryProducts {
  constructor() {
    this.swiperInstances = new Map();
    this.init();
  }

  init() {
    this.initIntersectionObserver();
    this.initExistingSwipers();
  }

  initIntersectionObserver() {
    const complementarySection = document.querySelector('.superfood-picks-section');
    
    if (!complementarySection) return;

    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;

      observer.unobserve(complementarySection);

      const url = complementarySection.dataset.url;
      console.log("Loading complementary products from:", url);

      fetch(url)
        .then(response => response.text())
        .then(text => {
          const html = document.createElement('div');
          html.innerHTML = text;
          
          // Look for the superfood picks section in the response
          const superfoodPicksResponse = html.querySelector('.superfood-picks-section');
          const recommendations = html.querySelector('.product-recommendations');

          if (superfoodPicksResponse && superfoodPicksResponse.innerHTML.trim().length) {
            // Use the superfood picks section content directly
            complementarySection.innerHTML = superfoodPicksResponse.innerHTML;
            
            // Initialize Swiper for superfood picks
            this.initComplementarySwiper();
          } else if (recommendations && recommendations.innerHTML.trim().length) {
            // Fallback: extract the swiper content from product-recommendations
            const swiperContent = recommendations.querySelector('.recommended-products-swiper');
            if (swiperContent) {
              complementarySection.querySelector('.superfood-picks-container').innerHTML = swiperContent.outerHTML;
              
              // Initialize Swiper for superfood picks
              this.initComplementarySwiper();
            }
          } else {
            // Hide section if no recommendations
            complementarySection.style.display = 'none';
          }
        })
        .catch(e => {
          console.error('Error loading complementary products:', e);
          complementarySection.style.display = 'none';
        });
    };

    const observer = new IntersectionObserver(handleIntersection, {
      rootMargin: '0px 0px 200px 0px'
    });

    observer.observe(complementarySection);
  }

  initExistingSwipers() {
    const swiperElements = document.querySelectorAll('.superfood-picks-section .superfood-picks-swiper');
    
    if (swiperElements.length === 0) return;

    swiperElements.forEach((element) => {
      this.initComplementarySwiper(element);
    });
  }

  initComplementarySwiper(container = null) {
    const swiperElement = container || 
      document.querySelector('.superfood-picks-section .superfood-picks-swiper');
    
    if (!swiperElement || swiperElement.swiper) return;

    const swiperId = swiperElement.getAttribute('data-swiper-id') || 
      `complementary-swiper-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    swiperElement.setAttribute('data-swiper-id', swiperId);

    const swiper = new Swiper(swiperElement, {
      slidesPerView: 'auto',
      spaceBetween: 20,
      navigation: {
        nextEl: document.querySelector('#superfood-picks-next'),
        prevEl: document.querySelector('#superfood-picks-prev'),
      },
      breakpoints: {
        320: {
          slidesPerView: 'auto',
          spaceBetween: 8,
        },
        480: {
          slidesPerView: 'auto',
          spaceBetween: 10,
        },
        768: {
          slidesPerView: 'auto',
          spaceBetween: 12,
        },
        1024: {
          slidesPerView: 'auto',
          spaceBetween: 15,
        }
      },
    });

    this.swiperInstances.set(swiperId, swiper);
  }

  // Public method to destroy all instances
  destroy() {
    this.swiperInstances.forEach((swiper) => {
      if (swiper && typeof swiper.destroy === 'function') {
        swiper.destroy(true, true);
      }
    });
    this.swiperInstances.clear();
  }

  // Public method to refresh all instances
  refresh() {
    this.swiperInstances.forEach((swiper) => {
      if (swiper && typeof swiper.update === 'function') {
        swiper.update();
      }
    });
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new ComplementaryProducts();
});

// Export for potential external use
export default ComplementaryProducts;
